import React, { useEffect } from 'react';
import { useTools, DEFAULT_TOOL } from '../../tools/Tools';
import useAppState from '../../AppStore';
import ToolDialogView from './ToolDialogView';
import { useTracking } from '../../utils/tracking/TrackingContext';
import Drawer from '../drawer/Drawer';
import HorizontalResizable from '../resizable/HorizontalResizable';
import useDisplaySize from '../../useDisplaySize';

const SmallScreenWrapper: React.FC<{
  children: React.ReactNode;
  displayInDrawer?: boolean;
}> = ({ children, displayInDrawer }) =>
  displayInDrawer ? (
    <Drawer>{children}</Drawer>
  ) : (
    <div className="tooldialog-mobile">
      <div className="tooldialog-mobile__wrapper">{children}</div>
    </div>
  );

const ToolDialog = () => {
  const { Tracking } = useTracking();
  const { isSmallScreen } = useDisplaySize();

  const activeTool = useAppState((state) => state.activeTool);
  const setActiveTool = useAppState((state) => state.setActiveTool);
  const activeView = useAppState((state) => state.activeView);
  const setActiveView = useAppState((state) => state.setActiveView);
  const toolDialogWidth = useAppState((state) => state.toolDialogWidth);
  const setToolDialogWidth = useAppState((state) => state.setToolDialogWidth);

  const tools = useTools();
  const tool = tools[activeTool];

  useEffect(() => {
    if (!tool) setActiveTool(DEFAULT_TOOL);
  }, [tool, setActiveTool]);

  const currentView =
    tool.views?.find((view) => view.key === activeView) ??
    tool.views?.find((view) => view.isMainView);
  if (!currentView) return null;

  const goToMainView = () => {
    const mainView = tool.views?.find((view) => view.isMainView);
    setActiveView(mainView?.key ?? '');
  };

  const handleResizeStop = () => {
    Tracking.UI.resizeToolDialog(toolDialogWidth);
  };

  const onClose = () => {
    setActiveTool(DEFAULT_TOOL);
  };

  const toolDialogView = (
    <ToolDialogView
      isMainView={currentView.isMainView}
      toolName={activeTool}
      viewName={currentView.key}
      key={currentView.key}
      label={currentView.label}
      viewComponent={currentView.viewComponent}
      onClose={onClose}
      goToMainView={goToMainView}
    />
  );

  return isSmallScreen ? (
    <SmallScreenWrapper displayInDrawer={currentView.displayInDrawer}>
      {toolDialogView}
    </SmallScreenWrapper>
  ) : (
    <HorizontalResizable
      initialWidth={toolDialogWidth}
      onResize={setToolDialogWidth}
      onResizeStop={handleResizeStop}
    >
      {toolDialogView}
    </HorizontalResizable>
  );
};

export default ToolDialog;
